import { FormControlLabel, Switch, Typography } from '@mui/material';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import Button from '@/components/CustomButtons/Button';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import loader from '@/assets/img/loader.svg';
import { useSupplierPortalSettingsForm } from './hooks/useSupplierPortalSettingsForm';

const SectionTitle = ({ children, sx }) => (
  <Typography
    variant='subtitle1'
    sx={{
      mt: 4,
      mb: 2,
      pb: 1,
      fontWeight: 600,
      textTransform: 'none',
      borderBottom: '1px solid',
      borderColor: 'divider',
      ...sx,
    }}>
    {children}
  </Typography>
);

const FIELD_LABELS = {
  token_url: 'Token URL',
  client_id: 'Client ID',
  client_secret: 'Client Secret',
  base_rest_url: 'Base REST URL',
  oauth_scope: 'OAuth Scope',
  client_website_url: 'Client Portal URL',
  client_website_label: 'Client Portal display label',
};

const humanizeFieldError = (fieldName, message) => {
  if (!message) return message;
  const label = FIELD_LABELS[fieldName];
  if (!label) return message;
  return message.replace(fieldName, label);
};

const SupplierPortalSettingsTab = ({ organizationId }) => {
  const {
    loading,
    saving,
    form,
    hasClientSecret,
    fieldErrors,
    generalErrors,
    updateField,
    setEnabled,
    handleUpdate,
  } = useSupplierPortalSettingsForm(organizationId);

  if (loading) {
    return (
      <div className='loaders' style={{ marginTop: '20px' }}>
        <img alt='loader' src={loader} />
      </div>
    );
  }

  return (
    <>
      <FormErrorSummary errors={generalErrors} />

      <GridContainer style={{ paddingTop: '30px' }}>
        <GridItem xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                name='enabled'
                color='primary'
              />
            }
            label='Enable Supplier Portal Lookup'
            sx={{
              my: 2,
              '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
            }}
          />
        </GridItem>
      </GridContainer>

      <SectionTitle>Client Portal Link</SectionTitle>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='Client Portal URL'
            placeholder='https://example.com'
            id='client_website_url'
            name='client_website_url'
            onChange={updateField}
            value={form.client_website_url}
            type='text'
            formControlProps={{ fullWidth: true }}
            error={!!fieldErrors.client_website_url}
            inputProps={{
              helperText: humanizeFieldError(
                'client_website_url',
                fieldErrors.client_website_url,
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='Client Portal display label'
            placeholder='Supplier Portal'
            id='client_website_label'
            name='client_website_label'
            onChange={updateField}
            value={form.client_website_label}
            type='text'
            formControlProps={{ fullWidth: true }}
            error={!!fieldErrors.client_website_label}
            inputProps={{
              helperText:
                humanizeFieldError(
                  'client_website_label',
                  fieldErrors.client_website_label,
                ) ||
                'Optional label shown on Create Payment. Defaults to Client website when empty.',
            }}
          />
        </GridItem>
      </GridContainer>

      <SectionTitle>Oracle FSCM Connection</SectionTitle>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='OAuth Token URL'
            id='token_url'
            name='token_url'
            onChange={updateField}
            value={form.token_url}
            type='text'
            formControlProps={{ fullWidth: true }}
            error={!!fieldErrors.token_url}
            inputProps={{
              helperText: humanizeFieldError(
                'token_url',
                fieldErrors.token_url,
              ),
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='Client ID'
            id='client_id'
            name='client_id'
            onChange={updateField}
            value={form.client_id}
            type='text'
            formControlProps={{ fullWidth: true }}
            error={!!fieldErrors.client_id}
            inputProps={{
              helperText: humanizeFieldError(
                'client_id',
                fieldErrors.client_id,
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='Client Secret'
            id='client_secret'
            name='client_secret'
            onChange={updateField}
            value={form.client_secret}
            type='password'
            formControlProps={{ fullWidth: true }}
            error={!!fieldErrors.client_secret}
            inputProps={{
              autoComplete: 'new-password',
              helperText:
                humanizeFieldError(
                  'client_secret',
                  fieldErrors.client_secret,
                ) ||
                (hasClientSecret
                  ? 'A secret is already saved. Leave blank to keep it, or enter a new value to replace it.'
                  : undefined),
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='FSCM REST Base URL'
            id='base_rest_url'
            name='base_rest_url'
            onChange={updateField}
            value={form.base_rest_url}
            type='text'
            formControlProps={{ fullWidth: true }}
            error={!!fieldErrors.base_rest_url}
            inputProps={{
              helperText: humanizeFieldError(
                'base_rest_url',
                fieldErrors.base_rest_url,
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText='OAuth Scope'
            id='oauth_scope'
            name='oauth_scope'
            onChange={updateField}
            value={form.oauth_scope}
            type='text'
            formControlProps={{ fullWidth: true }}
            error={!!fieldErrors.oauth_scope}
            inputProps={{
              helperText: humanizeFieldError(
                'oauth_scope',
                fieldErrors.oauth_scope,
              ),
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={6} sx={{ mt: 2 }}>
          <Button color='primary' onClick={handleUpdate} disabled={saving}>
            {saving ? 'Saving...' : 'Update'}
          </Button>
        </GridItem>
      </GridContainer>
    </>
  );
};

export default SupplierPortalSettingsTab;
